import React, { useMemo } from 'react';
import {
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  Typography,
  Box,
  Button
} from '@mui/material';
import { Translate } from 'react-localize-redux';
import Modal from '../../../../components/general/modal';
import { DocumentPDF } from '../../Document';
import { IconGenerator } from '../../../../components/general/mini_components';
import { sumCategoryBillingRows } from './financeTableTotals';



interface CategoryBillingSummary {
  totalRevenue: number;
  totalTests: number;
  averageTestCost: number;
  totalPending: number;
  totalPaid : number,
  totalDiscount: number,
  totalExternalPaymentPending: number,
  totalExternalPaymentPaid: number
}

interface CategoryItem {
    category: string;
    totalBilled: number;
    totalTests: number;
    averageTestCost: number;
    totalPending: number;
    totalPaid : number,
    count: number,
    totalDiscount: number,
    totalExternalPaymentPending: number,
    totalExternalPaymentPaid: number
}

// Legacy interface for backward compatibility
interface LegacyBillingItem {
  testName: string;
  department: string;
  count: number;
  unitPrice: number;
  discount: number;
  total: number;
  month: string;
}

// Accept any structure for now to handle different API formats
interface CategoryBillingTableProps {
  data: any;
  startDate?: number | null;
  endDate?: number | null;
}

const StatsCard = ({ title, value, isMonetary = false }: { title: string; value: number | string; isMonetary?: boolean }) => (
  <Card className="h-full">
    <CardContent>
      <Typography color="textSecondary" gutterBottom>
        {title}
      </Typography>
      <Typography variant="h5" component="div">
        {isMonetary ? `${value} FCFA` : value}
      </Typography>
    </CardContent>
  </Card>
);

const CategoryBillingTable = ({ data, startDate, endDate }: CategoryBillingTableProps) => {
  const [showPrintModal, setShowPrintModal] = React.useState(false);

  // Format dates for display
  const formatDateRange = () => {
    if (!startDate || !endDate) return '';
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    const formatDate = (date: Date) => {
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    };
    
    return `${formatDate(start)} - ${formatDate(end)}`;
  };

  // Process the data to extract items and summary
  const { processedItems, processedSummary } = useMemo(() => {
    // Default values
    const defaultSummary: CategoryBillingSummary = {
      totalRevenue: 0,
      totalTests: 0,
      averageTestCost: 0,
      totalPaid: 0,
      totalPending: 0,
      totalDiscount: 0,
      totalExternalPaymentPending: 0,
      totalExternalPaymentPaid: 0
    };

    // Check if data has the expected structure or try to adapt to the actual structure
    let items: CategoryItem[] = [];
    let summary = defaultSummary;

    if (!data) {
      console.error('No data provided to CategoryBillingTable');
      return { processedItems: [], processedSummary: defaultSummary };
    }

    // Extract items from data
    if (data.items && Array.isArray(data.items)) {
      // If data has the exact expected format
      items = data.items;
      summary = data.summary || defaultSummary;
    } else if (data.categoryTotals && typeof data.categoryTotals === 'object') {
      // Handle the new categoryTotals format
      items = Object.entries(data.categoryTotals).map(([category, stats]: [string, any]) => ({
        category: category,
        totalBilled: stats.totalBilled || 0,
        totalTests: 0, // Not available in this format
        averageTestCost: 0, // Will be calculated
        totalPaid: stats.totalPaid || 0,
        totalPending: stats.totalPending || 0,
        totalDiscount: stats.totalDiscount || 0,
        totalExternalPaymentPending: stats.totalExternalPaymentPending || 0,
        totalExternalPaymentPaid: stats.totalExternalPaymentPaid || 0,
        count: stats.count || 0,
      }));

      // Calculate summary from the items
      const totalRevenue = items.reduce((sum, item) => sum + item.totalBilled, 0);
      const totalTests = items.reduce((sum, item) => sum + item.count, 0);
      const totalPending = items.reduce((sum, item) => sum + item.totalPending, 0);
      const totalPaid = items.reduce((sum, item) => sum + item.totalPaid, 0);
      const totalDiscount = items.reduce((sum, item) => sum + item.totalDiscount, 0);
      const totalExternalPaymentPending = items.reduce((sum, item) => sum + item.totalExternalPaymentPending, 0);
      const totalExternalPaymentPaid = items.reduce((sum, item) => sum + item.totalExternalPaymentPaid, 0);
      
      summary = {
        totalRevenue,
        totalTests,
        averageTestCost: totalTests > 0 ? totalRevenue / totalTests : 0,
        totalPaid: totalPaid,
        totalPending: totalPending,
        totalDiscount: totalDiscount,
        totalExternalPaymentPending: totalExternalPaymentPending,
        totalExternalPaymentPaid: totalExternalPaymentPaid
      };
    } else if (data.series && Array.isArray(data.series)) {
      // Format from MultiChart component - convert to legacy format first
      const series = data.series;
      const categories = data.categories || [];

      // Try to transform series data into legacy format
      const legacyItems: LegacyBillingItem[] = series.flatMap((s: any, seriesIndex: number) => {
        const seriesName = s.name || `Series ${seriesIndex + 1}`;
        
        return (s.data || []).map((value: number, i: number) => ({
          testName: seriesName,
          department: categories[i] || `Department ${i + 1}`,
          count: 1,
          unitPrice: value,
          discount: 0,
          total: value,
          month: categories[i] || `Month ${i + 1}`
        }));
      });

      // Convert legacy items to CategoryItem format
      items = legacyItems.map((item, index) => ({
        category: item.testName || `Category ${index + 1}`,
        totalBilled: item.total,
        totalTests: item.count,
        averageTestCost: item.unitPrice,
        totalPending: 0,
        totalPaid: item.total,
        count: item.count,
        totalDiscount: item.discount,
        totalExternalPaymentPending: 0,
        totalExternalPaymentPaid: 0
      }));

      // Calculate summary
      const totalRevenue = items.reduce((sum, item) => sum + item.totalBilled, 0);
      const totalTests = items.reduce((sum, item) => sum + item.count, 0);
      
      summary = {
        totalRevenue,
        totalTests,
        averageTestCost: totalTests > 0 ? totalRevenue / totalTests : 0,
        totalPaid: totalRevenue,
        totalPending: 0,
        totalDiscount: 0,
        totalExternalPaymentPending: 0,
        totalExternalPaymentPaid: 0
      };
    } else if (Array.isArray(data)) {
      // Maybe it's a direct array of billing items - convert to legacy format first
      const legacyItems: LegacyBillingItem[] = data.map((item: any) => ({
        testName: item.name || item.testName || 'Unknown Test',
        department: item.department || 'Unknown',
        count: item.count || 1,
        unitPrice: item.price || item.unitPrice || item.value || 0,
        discount: item.discount || 0,
        total: item.total || item.value || 0,
        month: item.month || item.date || 'Unknown'
      }));

      // Convert legacy items to CategoryItem format
      items = legacyItems.map((item, index) => ({
        category: item.testName || `Category ${index + 1}`,
        totalBilled: item.total,
        totalTests: item.count,
        averageTestCost: item.unitPrice,
        totalPending: 0,
        totalPaid: item.total,
        count: item.count,
        totalDiscount: item.discount,
        totalExternalPaymentPending: 0,
        totalExternalPaymentPaid: 0
      }));

      // Calculate summary
      const totalRevenue = items.reduce((sum, item) => sum + item.totalBilled, 0);
      const totalTests = items.reduce((sum, item) => sum + item.count, 0);
      const totalDiscount = items.reduce((sum, item) => sum + item.totalDiscount, 0);
      
      summary = {
        totalRevenue,
        totalTests,
        averageTestCost: totalTests > 0 ? totalRevenue / totalTests : 0,
        totalPaid: totalRevenue - totalDiscount,
        totalPending: 0,
        totalDiscount: totalDiscount,
        totalExternalPaymentPending: 0,
        totalExternalPaymentPaid: 0
      };
    } else {
      // Fallback for when we can't determine the format
      console.error('Unknown data format for CategoryBillingTable', data);
      
      // Try to extract what we can
      const total = data.total || data.count || data.value || 0;
      summary = {
        totalRevenue: total,
        totalTests: 1,
        averageTestCost: total,
        totalPaid: total,
        totalPending: 0,
        totalDiscount: 0,
        totalExternalPaymentPending: 0,
        totalExternalPaymentPaid: 0
      };
    }

    return { processedItems: items, processedSummary: summary };
    }, [data]);

  // SHER-1033: one Total over every filtered category. The print path below reuses it
  // (SHER-1034) so the exported figure cannot drift from the screen.
  const totalRow = useMemo(
    () => sumCategoryBillingRows(processedItems),
    [processedItems],
  );

  const renderCategoryCells = (row: CategoryItem, bold: boolean = false) => (
    <>
      <TableCell sx={bold ? { fontWeight: 'bold' } : undefined}>
        {bold ? <Translate id="hospital.analytics.finance.total" /> : row.category}
      </TableCell>
      <TableCell align="right" sx={bold ? { fontWeight: 'bold' } : undefined}>{row.count.toLocaleString()}</TableCell>
      <TableCell align="right" sx={bold ? { fontWeight: 'bold' } : undefined}>{row.totalBilled.toLocaleString()} FCFA</TableCell>
      <TableCell align="right" sx={bold ? { fontWeight: 'bold' } : undefined}>{row.totalPaid.toLocaleString()} FCFA</TableCell>
      <TableCell align="right" sx={bold ? { fontWeight: 'bold' } : undefined}>{row.totalPending.toLocaleString()} FCFA</TableCell>
      <TableCell align="right" sx={bold ? { fontWeight: 'bold' } : undefined}>{row.totalDiscount.toLocaleString()} FCFA</TableCell>
      <TableCell align="right" sx={bold ? { fontWeight: 'bold' } : undefined}>{row.totalExternalPaymentPending.toLocaleString()} FCFA</TableCell>
      <TableCell align="right" sx={bold ? { fontWeight: 'bold' } : undefined}>{row.totalExternalPaymentPaid.toLocaleString()} FCFA</TableCell>
    </>
  );

  const renderPrintModal = () => {
    if (!showPrintModal) return null;
    
    return (
      <Modal key="modal" fullWidth medium open={showPrintModal} title="" closeModal={() => setShowPrintModal(false)}>
        <div>
          <style>
            {`
              #print {
                width: 100% !important;
                max-width: 1000px !important;
              }
              @media print {
                #print {
                  width: 100% !important;
                  max-width: none !important;
                }
              }
            `}
          </style>
          <DocumentPDF size="A4" name="Category Billing Report">
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h4" sx={{ textAlign: 'center', mb: 2, fontWeight: 'bold' }}>
                  <Translate id="hospital.analytics.finance.category_billing.title" />
                </Typography>
                {formatDateRange() && (
                  <Typography variant="h6" sx={{ textAlign: 'center', mb: 3, color: 'text.secondary' }}>
                    Report Period: {formatDateRange()}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12}>
                <div style={{ width: '100%', overflowX: 'auto' }}>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>{<Translate id="hospital.analytics.finance.category_billing.category" />}</strong></TableCell>
                          <TableCell align="right"><strong>{<Translate id="hospital.analytics.finance.category_billing.testsPerformed" />}</strong></TableCell>
                          <TableCell align="right"><strong>{<Translate id="hospital.analytics.finance.category_billing.totalRevenue" />}</strong></TableCell>
                          <TableCell align="right"><strong>{<Translate id="hospital.analytics.finance.category_billing.totalPaid" />}</strong></TableCell>
                          <TableCell align="right"><strong><Translate id="hospital.analytics.finance.category_billing.totalPending" /> </strong></TableCell>
                            <TableCell align="right"><strong>{<Translate id="hospital.analytics.finance.category_billing.totalDiscount" />}</strong></TableCell>
                          <TableCell align="right"><strong>{<Translate id="hospital.analytics.finance.category_billing.totalExternalPaymentPending" />}</strong></TableCell>
                          <TableCell align="right"><strong>{<Translate id="hospital.analytics.finance.category_billing.totalExternalPaymentPaid" />}</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {processedItems.map((row: CategoryItem) => (
                          <TableRow key={row.category}>
                            {renderCategoryCells(row)}
                          </TableRow>
                        ))}
                        <TableRow data-testid="finance-category-total-row">
                          {renderCategoryCells(totalRow, true)}
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </div>
              </Grid>
            </Grid>
          </DocumentPDF>
        </div>
      </Modal>
    );
  };

   
  const hasData = processedItems.length > 0;
  
  if (!hasData) {
      return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6">No laboratory billing data available</Typography>
          <Typography color="textSecondary">
          The data format may not be supported or no data was returned by the API.
          </Typography>
      </Box>
      );
  }

  return (
  <Box sx={{ width: '100%' }}>

    {/* Show monthly trends table only if there are multiple months */}
    {processedItems.length > 0 && (
      <>
        {/* Print Button */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h6">
              {<Translate id="hospital.analytics.finance.category_billing.title" />}
            </Typography>
            {formatDateRange() && (
              <Typography variant="body2" color="text.secondary">
                Report Period: {formatDateRange()}
              </Typography>
            )}
          </Box>
          <Button 
            onClick={() => setShowPrintModal(true)}
            startIcon={<IconGenerator type="pdf" />}
            variant="outlined"
            size="small"
          >
            <Translate id="general.print" />
          </Button>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>{<Translate id="hospital.analytics.finance.category_billing.category" />}</strong></TableCell>
                <TableCell align="right"><strong>{<Translate id="hospital.analytics.finance.category_billing.testsPerformed" />}</strong></TableCell>
                <TableCell align="right"><strong>{<Translate id="hospital.analytics.finance.category_billing.totalRevenue" />}</strong></TableCell>
                <TableCell align="right"><strong>{<Translate id="hospital.analytics.finance.category_billing.totalPaid" />}</strong></TableCell>
                <TableCell align="right"><strong><Translate id="hospital.analytics.finance.category_billing.totalPending" /> </strong></TableCell>
                  <TableCell align="right"><strong>{<Translate id="hospital.analytics.finance.category_billing.totalDiscount" />}</strong></TableCell>
                <TableCell align="right"><strong>{<Translate id="hospital.analytics.finance.category_billing.totalExternalPaymentPending" />}</strong></TableCell>
                <TableCell align="right"><strong>{<Translate id="hospital.analytics.finance.category_billing.totalExternalPaymentPaid" />}</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {processedItems.map((row: CategoryItem) => (
                <TableRow key={row.category}>
                  {renderCategoryCells(row)}
                </TableRow>
              ))}
              <TableRow data-testid="finance-category-total-row">
                {renderCategoryCells(totalRow, true)}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </>
    )}

    {/* Print Modal */}
    {renderPrintModal()}
  </Box>
);
};

export default CategoryBillingTable; 